/**
 * VOBackend — VO-lite (TECH_PLAN §7, the SLAM arc, step one).
 *
 * Rotation from DevicePose (corrected IMU quaternion); translation from sparse
 * feature flow (FeatureTracker, median displacement). Fragments live at world
 * positions with depth, so turning moves through them and stepping gives motion
 * parallax. It also exposes the tracked feature points for the 3D-scan overlay.
 *
 * frame: 'world', persistent: true — a session-local, drift-prone world frame
 * (no relocalizable map). This is the honest baseline the AlvaAR/WebXR backends
 * upgrade behind the same interface.
 */

import type { Anchor, CameraPose, Look, SpatialBackend } from './SpatialBackend.js';
import { DevicePose } from './DevicePose.js';
import { FeatureTracker, type TrackedPoint } from './FeatureTracker.js';
import type { FrameProvider } from './FrameProvider.js';
import { telemetry, type TrackingState } from './telemetry.js';

type Vec3 = { x: number; y: number; z: number };

const DEG = Math.PI / 180;
const DEPTH_MIN = 2;
const DEPTH_MAX = 7;
const TRANSLATION_SCALE = 3.2;
const SAMPLE_MS = 70;

function hashSeed(id: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function dir(azDeg: number, elDeg: number): Vec3 {
  const az = azDeg * DEG;
  const el = elDeg * DEG;
  const ce = Math.cos(el);
  return { x: ce * Math.sin(az), y: Math.sin(el), z: ce * Math.cos(az) };
}

/**
 * World-space camera displacement for one frame of feature flow.
 *
 * @param yawDeg `DevicePose.look().yaw`, i.e. `atan2(forward.x, forward.z)` of
 *   the camera's forward vector — so forward is `(sin h, ·, cos h)`.
 * @param dx,dy median feature flow, normalised (image x right, y DOWN)
 * @param k     units per normalised flow
 *
 * ## The sign, derived
 *
 * For a camera looking down −z with +y up, right = forward × up. With
 * forward = (sin h, 0, cos h) and up = (0, 1, 0) that is **(−cos h, 0, sin h)**.
 * (Check: the identity pose looks down −z, yaw = atan2(0, −1) = 180°, and the
 * formula gives right = (1, 0, 0) — correct.)
 *
 * ⚠️ This used `(cos h, 0, −sin h)`, which is camera-LEFT: every sideways step
 * moved the VO camera the wrong way, and with it all horizontal parallax.
 *
 * Stepping right makes the scene flow LEFT in the image (dx < 0), so the
 * camera moved along +right by −dx. Rising makes it flow DOWN (dy > 0, image y
 * grows downward), so the camera moved along +up by +dy.
 */
export function voTranslationStep(yawDeg: number, dx: number, dy: number, k: number): Vec3 {
  const h = yawDeg * DEG;
  const right: Vec3 = { x: -Math.cos(h), y: 0, z: Math.sin(h) };
  const up: Vec3 = { x: 0, y: 1, z: 0 };
  return {
    x: right.x * (-dx * k) + up.x * (dy * k),
    y: right.y * (-dx * k) + up.y * (dy * k),
    z: right.z * (-dx * k) + up.z * (dy * k),
  };
}

export class VOBackend implements SpatialBackend {
  readonly frame = 'world' as const;
  readonly persistent = true;

  private pose = new DevicePose();
  private position: Vec3 = { x: 0, y: 0, z: 0 };
  private anchors = new Map<string, Vec3>();
  private source: FrameProvider | null = null;
  private tracker: FeatureTracker | null = null;
  private sampleTimer: number | null = null;
  private trackConf = 0;
  private points: TrackedPoint[] = [];
  private state: TrackingState = 'idle';

  setFrameSource(source: FrameProvider | null): void {
    this.source = source;
    this.tracker?.reset();
  }

  async start(): Promise<void> {
    await this.pose.start();
    telemetry.setBackend('vo');
    try {
      this.tracker = new FeatureTracker();
      this.state = 'initializing';
      this.sampleTimer = window.setInterval(() => this.step(), SAMPLE_MS);
    } catch {
      this.tracker = null;
      this.state = 'unavailable';
    }
  }

  stop(): void {
    this.pose.stop();
    if (this.sampleTimer) window.clearInterval(this.sampleTimer);
    this.sampleTimer = null;
    this.tracker = null;
  }

  /** One tracking iteration. Also the unit the replay harness drives. */
  step(): void {
    if (!this.tracker || !this.source) return;

    const tGrab = performance.now();
    const r = this.tracker.update(this.source);
    const trackMs = performance.now() - tGrab;

    this.points = r.points;
    this.trackConf = this.trackConf * 0.7 + r.confidence * 0.3;

    if (r.confidence >= 0.2) {
      this.state = 'tracking';
      const step = voTranslationStep(this.pose.look().yaw, r.dx, r.dy, TRANSLATION_SCALE * r.confidence);
      this.position.x += step.x;
      this.position.y += step.y;
      this.position.z += step.z;
    } else {
      this.state = this.state === 'tracking' || this.state === 'lost' ? 'lost' : 'initializing';
    }

    const look = this.pose.look();
    telemetry.record({
      frameId: this.source.frameId(),
      state: this.state,
      featureCount: r.points.length,
      pos: [this.position.x, this.position.y, this.position.z],
      yaw: look.yaw,
      pitch: look.pitch,
      grabMs: 0,
      trackMs,
    });
  }

  trackingState(): TrackingState {
    return this.state;
  }

  resetTracking(): void {
    this.tracker?.reset();
    this.position = { x: 0, y: 0, z: 0 };
    this.state = 'reset';
    // Counted here, not via the 'reset' state, which step() overwrites before
    // anything is recorded. Also marks the jump back to the origin so it is not
    // measured as a step of the walk.
    telemetry.markReset();
  }

  look(): Look {
    return this.pose.look();
  }

  anchor(id: string): Anchor {
    if (!this.anchors.has(id)) {
      const s = hashSeed(id);
      const az = (s % 3600) / 10;
      const el = (((s >>> 12) % 500) / 10) - 25;
      const depth = DEPTH_MIN + ((s >>> 20) % 1000) / 1000 * (DEPTH_MAX - DEPTH_MIN);
      const d = dir(az, el);
      this.anchors.set(id, {
        x: this.position.x + d.x * depth,
        y: this.position.y + d.y * depth,
        z: this.position.z + d.z * depth,
      });
    }
    return this.resolve(id)!;
  }

  resolve(id: string): Anchor | null {
    const a = this.anchors.get(id);
    if (!a) return null;
    return { id, world: { x: a.x, y: a.y, z: a.z }, confidence: this.trackConf };
  }

  /**
   * Rotation from the IMU, translation from optical flow.
   *
   * The quaternion comes straight from `DevicePose` rather than being rebuilt
   * from `look()`: the Euler angles are derived FROM it, so going back the other
   * way can only lose information.
   */
  cameraPose(): CameraPose {
    return { position: { ...this.position }, orientation: this.pose.quaternion() };
  }

  confidence(): number {
    return this.trackConf;
  }

  featurePoints(): { x: number; y: number }[] {
    return this.points.map((p) => ({ x: p.x, y: p.y }));
  }
}
